export type ReplayCardHolding = {
  symbol: string;
  weight: number;
};

export type ReplayCardPoint = {
  index: number;
};

export type ReplayCardInput = {
  name: string;
  tag?: string;
  rangeLabel: string;
  startingValue?: number;
  endingValue?: number;
  returnPct?: number;
  asOf?: string;
  holdings: ReplayCardHolding[];
  points?: ReplayCardPoint[];
};

const cardColors = ["#b8f23a", "#8db8ff", "#f3ad65", "#c48ae8", "#ee8f9e", "#79d8c4"];
const moneyFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const cardCredit = "equityreplay.com";

function escapeXml(value: string) {
  return value.replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;" })[character] ?? character);
}

function trimText(value: string, maxLength: number) {
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 1).trimEnd()}…` : normalized;
}

function wrapTitle(value: string, lineLength = 25, maxLines = 2, maxLength = 48) {
  const words = trimText(value || "Historical portfolio", maxLength).split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (current && next.length > lineLength && lines.length < maxLines - 1) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines.slice(0, maxLines);
}

function formatMoney(value?: number) {
  if (!Number.isFinite(value)) return "—";
  return moneyFormatter.format(value as number);
}

function formatPercent(value?: number) {
  if (!Number.isFinite(value)) return "—";
  return `${(value as number) >= 0 ? "+" : ""}${(value as number).toFixed(1)}%`;
}

function formatCardDate(value?: string) {
  if (!value) return "HISTORICAL CLOSE-PRICE REPLAY";
  const normalized = value.replace(/\s+disclosure$/i, "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return `DATA THROUGH ${normalized}`;
  return `SOURCE AS OF ${trimText(normalized, 24).toUpperCase()}`;
}

function chartPath(points: ReplayCardPoint[], width = 500, height = 174) {
  const values: number[] = [];
  for (const point of points) {
    if (Number.isFinite(point.index)) values.push(point.index);
  }
  if (values.length < 2) return `M 0 ${height * .75} C ${width * .16} ${height * .62} ${width * .25} ${height * .72} ${width * .38} ${height * .48} S ${width * .62} ${height * .62} ${width * .76} ${height * .24} S ${width * .92} ${height * .38} ${width} ${height * .12}`;
  const min = Math.min(...values);
  const spread = Math.max(0.001, Math.max(...values) - min);
  return values.map((value, index) => {
    const x = (index / (values.length - 1)) * width;
    const y = height - ((value - min) / spread) * height * .72;
    return `${index === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(" ");
}

export function createReplayCardSvg(input: ReplayCardInput) {
  const width = 1200;
  const height = 630;
  const titleLines = wrapTitle(input.name);
  const holdings = input.holdings.filter((holding) => Number.isFinite(holding.weight) && holding.weight > 0).slice(0, 8);
  const total = holdings.reduce((sum, holding) => sum + holding.weight, 0) || 100;
  let barX = 80;
  const barSegments = holdings.map((holding, index) => {
    const segmentWidth = (holding.weight / total) * 1040;
    const segment = `<rect x="${barX.toFixed(2)}" y="480" width="${segmentWidth.toFixed(2)}" height="12" fill="${cardColors[index % cardColors.length]}"/>`;
    barX += segmentWidth;
    return segment;
  }).join("");
  const tickerLabels = holdings.slice(0, 8).map((holding, index) => {
    const column = index % 4;
    const row = Math.floor(index / 4);
    return `<text x="${80 + column * 260}" y="${520 + row * 28}" fill="#b8c0b0" font-family="IBM Plex Mono, monospace" font-size="15" letter-spacing="1">${escapeXml(holding.symbol)} <tspan fill="#f2f2e9" font-weight="600">${holding.weight.toFixed(1).replace(/\.0$/, "")}%</tspan></text>`;
  }).join("");
  const titleMarkup = titleLines.map((line, index) => `<text x="80" y="${174 + index * 64}" fill="#f2f2e9" font-family="Newsreader" font-size="60" font-weight="600" letter-spacing="-2">${escapeXml(line)}</text>`).join("");
  const chart = chartPath(input.points ?? []);
  const positive = (input.returnPct ?? 0) >= 0;
  const changeColor = positive ? "#b8f23a" : "#ee8f9e";
  const dateLabel = formatCardDate(input.asOf);
  const valueLabel = Number.isFinite(input.endingValue) ? formatMoney(input.endingValue) : "—";
  const changeLabel = Number.isFinite(input.returnPct) ? formatPercent(input.returnPct) : "—";

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="${width}" height="${height}" fill="#0b0e09"/>
  <path d="M0 118h1200M0 236h1200M0 354h1200M0 472h1200M240 0v630M480 0v630M720 0v630M960 0v630" fill="none" stroke="#dfe8d2" stroke-opacity=".08"/>
  <circle cx="1080" cy="82" r="160" fill="#b8f23a" fill-opacity=".08"/>
  <text x="80" y="66" fill="#b8f23a" font-family="IBM Plex Mono, monospace" font-size="16" font-weight="500" letter-spacing="3">${escapeXml(trimText(input.tag || "HISTORICAL PORTFOLIO", 34).toUpperCase())}</text>
  <text x="1120" y="66" text-anchor="end" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="14" letter-spacing="2">${cardCredit}</text>
  ${titleMarkup}
  <text x="80" y="304" fill="#aeb7a5" font-family="IBM Plex Sans, sans-serif" font-size="18">Close-price movement · weights applied once</text>
  <text x="80" y="330" fill="#aeb7a5" font-family="IBM Plex Sans, sans-serif" font-size="18">at the start of the selected window</text>
  <path d="M650 168h500M650 230h500M650 292h500" fill="none" stroke="#dfe8d2" stroke-opacity=".1" stroke-dasharray="5 10"/>
  <path d="${chart}" transform="translate(650 120)" fill="none" stroke="${changeColor}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
  <text x="1120" y="132" text-anchor="end" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="13" letter-spacing="2">${escapeXml(input.rangeLabel.toUpperCase())} VIEW</text>
  <text x="80" y="398" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="13" letter-spacing="2">ESTIMATED VALUE</text>
  <text x="80" y="444" fill="#f2f2e9" font-family="IBM Plex Mono, monospace" font-size="36" font-weight="500">${escapeXml(valueLabel)}</text>
  <text x="360" y="398" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="13" letter-spacing="2">HISTORICAL CHANGE</text>
  <text x="360" y="444" fill="${changeColor}" font-family="IBM Plex Mono, monospace" font-size="36" font-weight="500">${escapeXml(changeLabel)}</text>
  <text x="650" y="398" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="13" letter-spacing="2">STARTING VALUE</text>
  <text x="650" y="444" fill="#f2f2e9" font-family="IBM Plex Mono, monospace" font-size="28" font-weight="500">${escapeXml(formatMoney(input.startingValue))}</text>
  ${barSegments}
  ${tickerLabels}
  <text x="80" y="606" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="12" letter-spacing="2">${escapeXml(dateLabel)} · ILLUSTRATIVE ONLY · NO TRADES OR REBALANCING</text>
</svg>`;
}

export function createReplayCardPortraitSvg(input: ReplayCardInput) {
  const width = 1080;
  const inset = 96;
  const contentWidth = width - inset * 2;
  const titleLines = wrapTitle(input.name, 24, 3, 64);
  const holdings = input.holdings.filter((holding) => Number.isFinite(holding.weight) && holding.weight > 0).slice(0, 8);
  const total = holdings.reduce((sum, holding) => sum + holding.weight, 0) || 100;
  let barX = inset;
  const subtitleY = 270 + titleLines.length * 78 + 32;
  const chartY = Math.max(570, subtitleY + 80);
  const chartHeight = 230;
  const metricsTop = chartY + chartHeight + 42;
  const metricsLabelY = metricsTop + 38;
  const metricsLabelSecondY = metricsTop + 60;
  const metricsValueY = metricsTop + 112;
  const metricsBottom = metricsTop + 142;
  const barY = metricsTop + 166;
  const holdingsTop = barY + 74;
  const holdingsRows = Math.max(1, Math.ceil(holdings.length / 2));
  const footerY = holdingsTop + holdingsRows * 58 + 58;
  const height = Math.max(1480, footerY + 150);
  const barSegments = holdings.map((holding, index) => {
    const segmentWidth = (holding.weight / total) * contentWidth;
    const segment = `<rect x="${barX.toFixed(2)}" y="${barY}" width="${segmentWidth.toFixed(2)}" height="16" fill="${cardColors[index % cardColors.length]}"/>`;
    barX += segmentWidth;
    return segment;
  }).join("");
  const tickerLabels = holdings.map((holding, index) => {
    const column = index % 2;
    const row = Math.floor(index / 2);
    const symbolX = column === 0 ? inset : 560;
    const weightX = column === 0 ? 410 : width - inset;
    const y = holdingsTop + row * 58;
    return `<text x="${symbolX}" y="${y}" fill="#b8c0b0" font-family="IBM Plex Mono, monospace" font-size="19" letter-spacing="1">${escapeXml(holding.symbol)}</text><text x="${weightX}" y="${y}" text-anchor="end" fill="#f2f2e9" font-family="IBM Plex Mono, monospace" font-size="19" font-weight="600">${holding.weight.toFixed(1).replace(/\.0$/, "")}%</text>`;
  }).join("");
  const titleMarkup = titleLines.map((line, index) => `<text x="${inset}" y="${270 + index * 78}" fill="#f2f2e9" font-family="Newsreader" font-size="70" font-weight="600" letter-spacing="-2">${escapeXml(line)}</text>`).join("");
  const chart = chartPath(input.points ?? [], contentWidth, chartHeight);
  const gridLines = [0, 0.33, 0.66, 1].map((ratio) => `<path d="M${inset} ${chartY + chartHeight * ratio}H${width - inset}" fill="none" stroke="#dfe8d2" stroke-opacity=".1" stroke-dasharray="4 8"/>`).join("");
  const backgroundGrid = [220, 440, 660, 880, 1100, 1320, height - 80].map((y) => `<path d="M0 ${y}H${width}"/>`).join("");
  const verticalGrid = [270, 540, 810].map((x) => `<path d="M${x} 0V${height}"/>`).join("");
  const positive = (input.returnPct ?? 0) >= 0;
  const changeColor = positive ? "#b8f23a" : "#ee8f9e";
  const dateLabel = formatCardDate(input.asOf);
  const valueLabel = Number.isFinite(input.endingValue) ? formatMoney(input.endingValue) : "—";
  const changeLabel = Number.isFinite(input.returnPct) ? formatPercent(input.returnPct) : "—";
  const metricXs = [inset, 395, 694];

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="${width}" height="${height}" fill="#0b0e09"/>
  <rect x="40" y="40" width="1000" height="${height - 80}" rx="32" fill="none" stroke="#2a3323" stroke-width="2"/>
  <g fill="none" stroke="#dfe8d2" stroke-opacity=".07">${backgroundGrid}${verticalGrid}</g>
  <circle cx="870" cy="150" r="230" fill="#b8f23a" fill-opacity=".08"/>
  <text x="${inset}" y="120" fill="#b8f23a" font-family="IBM Plex Mono, monospace" font-size="17" font-weight="500" letter-spacing="3">${escapeXml(trimText(input.tag || "HISTORICAL PORTFOLIO", 34).toUpperCase())}</text>
  <text x="${width - inset}" y="120" text-anchor="end" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="16" letter-spacing="2">${cardCredit}</text>
  ${titleMarkup}
  <text x="${inset}" y="${subtitleY}" fill="#aeb7a5" font-family="IBM Plex Sans, sans-serif" font-size="22">Close-price movement · weights applied once</text>
  <text x="${inset}" y="${subtitleY + 34}" fill="#aeb7a5" font-family="IBM Plex Sans, sans-serif" font-size="22">at the start of the selected window</text>
  ${gridLines}
  <path d="${chart}" transform="translate(${inset} ${chartY})" fill="none" stroke="${changeColor}" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>
  <text x="${width - inset}" y="${chartY - 22}" text-anchor="end" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="15" letter-spacing="2">${escapeXml(input.rangeLabel.toUpperCase())} VIEW</text>
  <path d="M${inset} ${metricsTop}H${width - inset}M${inset} ${metricsBottom}H${width - inset}" fill="none" stroke="#dfe8d2" stroke-opacity=".14"/>
  <text x="${metricXs[0]}" y="${metricsLabelY}" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="15" letter-spacing="2">ESTIMATED</text>
  <text x="${metricXs[0]}" y="${metricsLabelSecondY}" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="15" letter-spacing="2">VALUE</text>
  <text x="${metricXs[0]}" y="${metricsValueY}" fill="#f2f2e9" font-family="IBM Plex Mono, monospace" font-size="36" font-weight="500">${escapeXml(valueLabel)}</text>
  <text x="${metricXs[1]}" y="${metricsLabelY}" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="15" letter-spacing="2">HISTORICAL</text>
  <text x="${metricXs[1]}" y="${metricsLabelSecondY}" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="15" letter-spacing="2">CHANGE</text>
  <text x="${metricXs[1]}" y="${metricsValueY}" fill="${changeColor}" font-family="IBM Plex Mono, monospace" font-size="36" font-weight="500">${escapeXml(changeLabel)}</text>
  <text x="${metricXs[2]}" y="${metricsLabelY}" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="15" letter-spacing="2">STARTING</text>
  <text x="${metricXs[2]}" y="${metricsLabelSecondY}" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="15" letter-spacing="2">VALUE</text>
  <text x="${metricXs[2]}" y="${metricsValueY}" fill="#f2f2e9" font-family="IBM Plex Mono, monospace" font-size="32" font-weight="500">${escapeXml(formatMoney(input.startingValue))}</text>
  ${barSegments}
  ${tickerLabels}
  <text x="${inset}" y="${footerY}" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="14" letter-spacing="2">${escapeXml(dateLabel)}</text>
  <text x="${inset}" y="${footerY + 49}" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="14" letter-spacing="2">ILLUSTRATIVE ONLY · NO TRADES OR REBALANCING</text>
</svg>`;
}

/** Create the generic, data-free social preview used by the homepage and editorial pages. */
export function createSiteCardSvg() {
  const width = 1200;
  const height = 630;
  const chart = chartPath([
    { index: 100 },
    { index: 108 },
    { index: 104 },
    { index: 119 },
    { index: 116 },
    { index: 132 },
  ], 460, 150);
  const featureLabels = ["STOCKS", "ALLOCATIONS", "TIME WINDOWS"];
  const featureMarkup = featureLabels.map((label, index) => {
    const x = 80 + index * 226;
    const separator = index > 0 ? `<path d="M${x - 38} 492v42" stroke="#dfe8d2" stroke-opacity=".18"/>` : "";
    return `${separator}<text x="${x}" y="520" fill="#f2f2e9" font-family="IBM Plex Mono, monospace" font-size="15" letter-spacing="2">${label}</text>`;
  }).join("");

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="${width}" height="${height}" fill="#0b0e09"/>
  <path d="M0 126h1200M0 252h1200M0 378h1200M0 504h1200M240 0v630M480 0v630M720 0v630M960 0v630" fill="none" stroke="#dfe8d2" stroke-opacity=".08"/>
  <circle cx="1080" cy="86" r="170" fill="#b8f23a" fill-opacity=".08"/>
  <text x="80" y="68" fill="#b8f23a" font-family="IBM Plex Mono, monospace" font-size="16" font-weight="500" letter-spacing="3">EQUITY REPLAY</text>
  <text x="1120" y="68" text-anchor="end" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="14" letter-spacing="2">${cardCredit}</text>
  <text x="80" y="196" fill="#f2f2e9" font-family="Newsreader" font-size="68" font-weight="600" letter-spacing="-2">Replay the market’s past.</text>
  <text x="80" y="278" fill="#aeb7a5" font-family="IBM Plex Sans, sans-serif" font-size="22">See how a stock allocation would have moved across</text>
  <text x="80" y="312" fill="#aeb7a5" font-family="IBM Plex Sans, sans-serif" font-size="22">months or years—with no account and no trade.</text>
  <path d="M650 172h470M650 232h470M650 292h470" fill="none" stroke="#dfe8d2" stroke-opacity=".1" stroke-dasharray="5 10"/>
  <path d="${chart}" transform="translate(650 126)" fill="none" stroke="#b8f23a" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
  <circle cx="1110" cy="182" r="5" fill="#b8f23a"/>
  <text x="1120" y="128" text-anchor="end" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="13" letter-spacing="2">CLOSE-PRICE REPLAY</text>
  <text x="1120" y="326" text-anchor="end" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="12" letter-spacing="2">1M · 6M · 1Y · 3Y · 5Y · 10Y</text>
  <path d="M80 456H1120" stroke="#dfe8d2" stroke-opacity=".18"/>
  ${featureMarkup}
  <text x="80" y="598" fill="#7c8875" font-family="IBM Plex Mono, monospace" font-size="12" letter-spacing="2">HISTORICAL SIMULATOR · ILLUSTRATIVE ONLY · NO TRADES OR REBALANCING</text>
</svg>`;
}
